import { GlobalRole } from '@itm/shared-frontend';

export { ResultFailType } from '@itm/shared-frontend';
export type {
  GlobalRole,
  SelectOption,
  ServerError,
  ServerFormErrors,
  ExternalFilters,
  TanstackTableChangeParams,
  // Data Access Permission
  ErrorWithType,
  SchemeFromProductDto,
  ProductDto,
  CreateDataAccessDto,
  ShortSchemeResponse,
  ProductSchemeResponse,
} from '@itm/shared-frontend/lib/types';

export enum TargetPlatform {
  AWS = 'AWS',
  AZURE = 'AZURE',
}

export enum Role {
  SuperAdmin = 'SuperAdmin',
  Support = 'Support',
  Analyst = 'FlexPortal_Analyst',
  ClientAdmin = 'FlexPortal_ClientAdmin',
  Manager = 'FlexPortal_Manager',
  EmployerAdmin = 'FlexPortal_EmployerAdmin',
}

export enum DataProcessPeriodFrequency {
  Month = 'Month',
  Quarter = 'Quarter',
  Year = 'Year',
}

export enum DataProcessStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Rejected = 'Rejected',
  Failed = 'Failed',
  FailedActive = 'FailedActive',
  Cancelled = 'Cancelled',
  Archived = 'Archived',
}

export enum SummaryDataProcessStatus {
  NotCreated = 'NotCreated',
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Rejected = 'Rejected',
  Cancelled = 'Cancelled',
  Failed = 'Failed',
  Archived = 'Archived',
  Completing = 'Completing',
}

export enum DataProcessUnderwritingStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
}

export enum DataProcessBenefitAnalysisStatus {
  NotStarted = 'NotStarted',
  InProgressInitial = 'InProgressInitial',
  InProgressMembers = 'InProgressMembers',
  InProgressAnalysis = 'InProgressAnalysis',
  InProgressFinal = 'InProgressFinal',
  Completed = 'Completed',
  CompletedWithWarnings = 'CompletedWithWarnings',
  Failed = 'Failed',
  FailedValidation = 'FailedValidation',
}

export enum DataProcessFormatCheckStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Completed = 'Completed',
  CompletedWithWarnings = 'CompletedWithWarnings',
  Failed = 'Failed',
  FailedValidation = 'FailedValidation',
}

export enum DataProcessSubmitStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
}

export enum UntanglerAnalysisLevel {
  Error = 'Error',
  Warning = 'Warning',
  Info = 'Info',
  Debug = 'Debug',
}

export enum DataProcessStage {
  FileUpload = 'FileUpload',
  MemberChecks = 'MemberChecks',
  BenefitAnalysis = 'BenefitAnalysis',
  UnderwritingUpdate = 'UnderwritingUpdate',
  InvoiceGeneration = 'InvoiceGeneration',
  ProcessCompletion = 'ProcessCompletion',
  ProcessRejection = 'ProcessRejection',
}

export enum DataProcessStageStatus {
  Started = 'Started',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
}

export enum InvoiceStatus {
  Success = 'Success',
  Failed = 'Failed',
  InProgress = 'InProgress',
}

export enum ReportStatus {
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
}

export enum ReportType {
  Generated = 'Generated',
  Uploaded = 'Uploaded',
}

export type DataProcessDetailsUrlSearchParams = {
  processId: string;
};

export type DataUploadFormUrlSearchParams = {
  employerId: string;
  policyId: string;
  startDate: string;
};

export type BenefitAnalysisUrlSearchParams = DataProcessDetailsUrlSearchParams;

export type DataProcessSummaryUrlSearchParams = DataUploadFormUrlSearchParams;

export type CompanyResponse = {
  id: string;
  name: string | null;
};

export type SchemeResponse = {
  id: string;
  companyId: string;
  name: string;
  isPartnerScheme: boolean;
  isUnderwritingEnabled: boolean;
  isPartnerReportEnabled: boolean;
};

export type UpdatePartnerDetailsRequest = {
  isPartnerScheme: boolean;
  isUnderwritingEnabled: boolean;
  isPartnerReportEnabled: boolean;
};

export type TemplateResponse = {
  id: string;
  name: string;
};

export type SchemeDataProcessSettingsDto = {
  id: string;
  schemeId: string;
  templateName: string;
};

export type CompanyDataProcessSettingsDto = {
  id: string;
  companyId: string;
  defaultTemplateName: string;
  schemes: SchemeDataProcessSettingsDto[];
};

export type CreateSchemeDataProcessSettingsDto = {
  schemeId: string;
  templateName?: string;
};

export type CreateCompanyDataProcessSettingsDto = {
  defaultTemplateName: string;
  schemes?: CreateSchemeDataProcessSettingsDto[];
};

export type UpdateSchemeDataProcessSettingsDto = {
  id?: string;
  schemeId: string;
  templateName?: string;
};

export type UpdateCompanyDataProcessSettingsDto = {
  id: string;
  defaultTemplateName: string;
  schemes?: UpdateSchemeDataProcessSettingsDto[];
};

export type DataProcessDto = {
  id: string;
  schemeId: string;
  employerId: string;
  policyId: string;
  processNumber: number;
  period: DataProcessPeriodFrequency;
  periodStartDate: string;
  uploadedBy: string;
  status: DataProcessStatus;
  benefitAnalysisStatus: DataProcessBenefitAnalysisStatus;
  underwritingStatus: DataProcessUnderwritingStatus;
  formatCheckStatus: DataProcessFormatCheckStatus;
  createdDate: string;
  isUnderwritingEnabled: boolean;
  isPartnerReportEnabled: boolean;
};

export type UserPermissionResponse = {
  userId: string;
  companyId: string;
  companyName: string;
  schemeId: string;
  schemeName: string;
  productId: string;
  productName: string;
  productRoleId: string;
  productRoleName: GlobalRole;
  userProductRoleId: string;
  assignmentDate: string;
};

export type ProductInfoResponse = {
  id: string;
  description: string | null;
  version: string | null;
};

export type UpdateProductInfoModel = {
  description?: string;
};

export type EmployerResponse = {
  id: string;
  name: string;
  address1: string;
  address2: string | null;
  address5: string | null;
  address4: string | null;
  address3: string | null;
  postcode: string;
  email: string;
  epsr: string;
  companiesHouseNumber: string;
  charityNumber: string;
};

export type DataProcessSummaryDto = {
  id: string | null;
  status: SummaryDataProcessStatus;
  periodStartDate: string;
  isActive: boolean;
  totalRecordsProcessed: number;
  totalAmount: number;
  totalFlexBenefit: number;
  policyId: string;
  reportHistoryId?: string;
};

export type SummaryRangeItemDto = {
  date: string;
  isDefault: boolean;
};

export enum TemplateStatus {
  Draft = 'Draft',
  Published = 'Published',
  Archived = 'Archived',
}

export enum TemplateType {
  Default = 'Default',
  ClientSpecific = 'ClientSpecific',
}

export type TemplateSearchResponse = {
  id: string;
  name: string;
  version: number;
  createdDate: string;
  updatedDate: string | null;
  createByUserName: string;
  updatedByUserName: string | null;
  status: TemplateStatus;
  type: TemplateType;
  allowToEdit: boolean;
};

export type TemplateSearchResponsePagedResponse = {
  totalCount: number;
  items: TemplateSearchResponse[] | null;
};

export type DataProcessAnalysisDto = {
  id: string;
  memberId: string | null;
  surname: string | null;
  rowIdentified: number;
  columnName: string | null;
  valueValidated: string | null;
  ruleUsed: string | null;
  message: string | null;
  validationType: UntanglerAnalysisLevel;
  errorCode: string;
};

export type DataProcessAnalysisDtoPagedResponse = {
  totalCount: number;
  items: DataProcessAnalysisDto[] | null;
};

export type DataProcessAnalysisSummaryDto = {
  numberOfErrors: number;
  numberOfWarnings: number;
  numberOfInfos: number;
  totalNumberOfEntries: number;
};

export type BenefitAnalysisResponse = {
  id: string;
  errorNumber: number;
  warningNumber: number;
  infoNumber: number;
  totalEntriesNumber: number;
  totalAmount: number;
  totalFlexBenefit: number;
  dueDate?: string;
  isUnderwritingAvailable: boolean;
};

export type UnderwritingCodeSummaryDto = {
  code: string;
  count: number;
};

export type UnderwritingSummaryDto = {
  canSkip: boolean;
  breakdown: UnderwritingCodeSummaryDto[];
};

export type DataProcessMemberUnderwritingDto = {
  dataProcessMemberId: string;
  rowNumber: number;
  memberId: string;
  surname: string | null;
  underwritingStatus: string | null;
  isTemporaryCoverAvailable: boolean;
  loadedPremium: number | null;
};

export enum UnderwritingStatus {
  TemporaryCover = 'Temporary Cover',
  Accepted = 'Accepted',
  Restricted = 'Restricted',
  Postponed = 'Postponed',
  Declined = 'Declined',
}

export type DataProcessMemberUnderwritingItemRequest = {
  dataProcessMemberId: string;
  rowNumber: number;
  memberId: string;
  underwritingStatus?: string;
  loadedPremium?: number;
  reason?: string;
};

export type TriggerUnderwritingRequestBody = {
  underwritingItems: DataProcessMemberUnderwritingItemRequest[];
};

export type UnderwritingMemberFormData = DataProcessMemberUnderwritingItemRequest & {
  surname: string;
  isTemporaryCoverAvailable: boolean;
  isDiscretionary: boolean;
};

export type UnderwritingFormData = {
  members: UnderwritingMemberFormData[];
};

export type DataProcessAnalysisItemDto = {
  type: UntanglerAnalysisLevel;
  message: string;
  errorCode: string;
};

export type DataProcessAnalysisColumnDto = {
  name: string;
  displayName: string;
  errors: DataProcessAnalysisItemDto[];
  isFrozen: boolean;
};

export type DataProcessAnalysisCellDto = {
  value: string;
  errors: DataProcessAnalysisItemDto[] | null;
  isFrozen: boolean;
};

export type DataProcessAnalysisRowDto = {
  data: {
    [key: string]: DataProcessAnalysisCellDto;
  };
  errors: DataProcessAnalysisItemDto[] | null;
};

export type DataProcessAnalysisDetailedDtoPagedResponse = {
  totalCount: number;
  items: DataProcessAnalysisRowDto[] | null;
  columns: DataProcessAnalysisColumnDto[];
};

export type BenefitAnalysisDetailedDtoPagedResponse = {
  totalCount: number;
  items: DataProcessAnalysisRowDto[] | null;
};

export type TemplateHistoryResponse = {
  id: string;
  name: string;
  version: number;
  updatedDate: string | null;
  user: string;
  createdByUserId: string;
  status: TemplateStatus;
  devExpressReport: string;
  templateId: string;
};

export type ReportingTemplateResponse = {
  id: string;
  name: string;
  version: number;
  createdDate: string;
  updatedDate: string | null;
  createdBy: string;
  status: TemplateStatus;
  type: TemplateType;
  devExpressReport: string;
  assignedSchemes: SchemeResponse[];
  templateHistories: TemplateHistoryResponse[];
};

export type CreateTemplateModel = {
  name: string;
  schemeIds?: string[];
};

export type UpdateTemplateModel = {
  templateId: string;
  name: string;
  schemeIds?: string[];
};

export type SaveAsTemplateModel = {
  name?: string;
  templateId: string;
};

export type TemplateHistorySearchResponse = {
  id: string;
  name: string | null;
  version: number | null;
  createdDate: string;
  createdBy: string | null;
  type: TemplateType;
  status: TemplateStatus;
  updatedBy: string | null;
  updatedDate: string | null;
};

export type TemplateHistorySearchResponsePagedResponse = {
  totalCount: number;
  items: TemplateHistorySearchResponse[];
};

export type CreatePolicyRequest = {
  employerId: string;
  schemeId: string;
  number: string;
  startDate: string;
  renewalFrequency?: number;
  firstPeriodDate?: string;
  parentPolicyId?: string;
};

export type UpdatePolicyRequest = {
  parentPolicyId: string | null;
};

export enum PolicyStatus {
  Active = 'Active',
  Cancelled = 'Cancelled',
}

export enum SchemeConfigurationType {
  BenefitParameters = 'BenefitParameters',
  BenefitValidations = 'BenefitValidations',
  BenefitCalculation = 'BenefitCalculation',
}

export type SchemeConfigurationDto = {
  schemeId: string;
  schemeName: string;
  configurationType: SchemeConfigurationType;
  createdDate: string;
  updatedDate: string | null;
};

export enum ValidationTypeSeverity {
  Error = 'Error',
  Warning = 'Warning',
  Info = 'Info',
  Debug = 'Debug',
}

export type ValidationTypeDto = {
  errorCode: string;
  friendlyMessage: string;
  parameters: string[] | null;
  severity: ValidationTypeSeverity;
  category: string;
};

export type PolicyUIParameterItemDto = {
  shortCode: string;
  friendlyName: string;
  order: number;
  isOptional: boolean;
  optionalCondition: string | null;
};

export type PolicyUIParameterSubGroupDto = {
  subGroupHeading: string;
  order: number;
  parameters: PolicyUIParameterItemDto[];
};

export type PolicyUIParameterGroupDto = {
  groupHeading: string;
  order: number;
  subGroups: PolicyUIParameterSubGroupDto[];
};

export type CalculationMappingDto = {
  lumeraCoreParameter: string;
  untanglerResultColumn: string;
};

export type BenefitCalculationDto = {
  baProductExternalId: string;
  baExternalId: string;
  calculationMappings: CalculationMappingDto[];
};

export type PolicyResponseDto = {
  employerName: string;
  schemeName: string;
  policyId: string;
  employerId: string;
  schemeId: string;
  policyNumber: string;
  renewalMonthFrequency: number;
  policyStatus: PolicyStatus;
  policyStartDate: string;
  renewalDate: string;
  firstPeriodDate: string;
  createdDate: string;
  updatedDate: string | null;
  isPartnerScheme: boolean;
  parentPolicyId: string | null;
  parentPolicyName: string | null;
};

export type PolicyResponseDtoPagedResponse = {
  totalCount: number;
  items: PolicyResponseDto[];
};

export type DataProcessAnalysisDetailsDto = {
  processNumber: number;
  employerId: string;
  schemeId: string;
  policyId: string;
  employerName: string;
  schemeName: string;
  policyNumber: string;
  processingPeriod: string;
  uploadedDate: string;
  uploadedBy: string;
  processStatus: SummaryDataProcessStatus;
  rejectionReason: string | null;
  fileName: string;
  formatCheckStatus: DataProcessFormatCheckStatus;
  benefitAnalysisStatus: DataProcessBenefitAnalysisStatus;
  submitStatus?: DataProcessSubmitStatus;
  errorMessage?: string | null;
  isUnderwritingEnabled: boolean;
};

export enum PolicyParameterDataType {
  Number = 'Number',
  Date = 'Date',
  Boolean = 'Boolean',
  Complex = 'Complex',
}

export type AvailableValue = {
  value: string;
  displayValue: string;
};

export type PolicyParameterValueResponse = {
  shortCode: string;
  dataType: PolicyParameterDataType;
  value: string | null;
  availableValues: AvailableValue[] | null;
};

export type PolicyParameterResponse = {
  shortCode: string;
  friendlyName: string | null;
  dataType: PolicyParameterDataType;
  value: string;
  availableValues: AvailableValue[] | null;
  order: number;
  isOptional: boolean;
  optionalCondition: string | null;
};

export type PolicyParameterDto = {
  shortCode: string;
  value: string;
};

export enum PolicyAuditType {
  Parameters = 'Parameters',
  GeneralDetails = 'GeneralDetails',
}

export enum EntityConfigurationAuditType {
  Policy = 'Policy',
  Entities = 'Entities',
}

export enum AuditAction {
  Created = 'Created',
  Updated = 'Updated',
  Deleted = 'Deleted',
}

export type PolicyAuditResponseDto = {
  id: string;
  changedBy: string;
  dateTime: string;
  type: PolicyAuditType;
  action: AuditAction;
};

export type ExtendedPolicyAuditResponseDto = PolicyAuditResponseDto & {
  schemeName: string;
  employerName: string;
  policyNumber: string;
  previousValues: string;
  newValues: string;
  appliesFromDate: string | null;
};

export type EntityConfigurationAuditResponseDto = {
  id: string;
  referenceName: string;
  employerName: string;
  changedBy: string;
  dateTime: string;
  type: EntityConfigurationAuditType;
  action: AuditAction;
  previousValues: string;
  newValues: string;
};

export type EntityConfigurationAuditResponseDtoPagedResponse = {
  totalCount: number;
  items: EntityConfigurationAuditResponseDto[] | null;
};

export type SchemeConfigurationAuditResponseDto = {
  id: string;
  changedBy: string;
  dateTime: string;
  type: SchemeConfigurationType;
  action: AuditAction;
};

export type SchemeConfigurationAuditResponseDtoPagedResponse = {
  totalCount: number;
  items: SchemeConfigurationAuditResponseDto[] | null;
};

export type DataProcessAuditResponseDto = {
  id: string;
  name: DataProcessStage;
  status: DataProcessStageStatus;
  createdDate: string;
  description: string | null;
};

export type DataProcessAuditResponseDtoPagedResponse = {
  totalCount: number;
  items: DataProcessAuditResponseDto[] | null;
};

export enum DataProcessReportingState {
  CanGenerate = 'CanGenerate',
  InProgress = 'InProgress',
}

export type EmployerEntityOptionDto = {
  id: string;
  name: string;
  code?: string;
  memberCount: number;
};

export type DataProcessReportingStateDto = {
  state: DataProcessReportingState;
  canSplitByEntity: boolean;
  canSplitByTrustType: boolean;
  availableEntities: EmployerEntityOptionDto[];
  availableTemplates: TemplateResponse[];
  isTemplatePublished: boolean;
};

export type InvoiceHistoryDto = {
  id: string;
  processNumber: number;
  employerName: string;
  policyName: string;
  runBy: string;
  status: InvoiceStatus;
  createdDate: string;
};

export type ReportHistorySearchResponseDto = {
  id: string;
  reportName: string;
  processNumber: number;
  employerName: string;
  historyItemsCount: number;
  schemeName: string;
  policyNumber: string;
  periodStartDate: string;
  status: ReportStatus;
  type: ReportType;
  createdDate: string;
  createdBy: string;
};

export type ReportHistorySearchResponseDtoPagedResponse = {
  totalCount: number;
  items: ReportHistorySearchResponseDto[] | null;
};

export type CompleteDataProcessRequestDto = {
  templateId: string;
  splitByEntity?: boolean;
  splitByTrustType?: boolean;
  splitEntities?: string[];
};

export type CreateReportModel = {
  dataProcessId: string;
  templateId: string;
  reportingDetails?: CompleteDataProcessRequestDto;
};

export type ReportHistoryDetailsResponse = {
  id: string;
  reportName: string;
  processNumber: number;
  employerName: string;
  schemeName: string;
  policyNumber: string;
  periodStartDate: string;
  runBy: string;
  status: ReportStatus;
  type: ReportType;
  errorMessage: string | null;
  createdDate: string;
};

export type ReportDownloadAuditDto = {
  user: string;
  date: string;
};

export type ReportDownloadAuditDtoPagedResponse = {
  totalCount: number;
  items: ReportDownloadAuditDto[] | null;
};

export enum ReportHistoryItemType {
  General = 'General',
  Split = 'Split',
}

export type ReportHistoryItemSearchResponse = {
  id: string;
  entityName?: string;
  trustType?: string;
  invoiceName: string;
  type: ReportHistoryItemType;
  templateName: string;
};

export type ReportHistoryItemSearchResponsePagedResponse = {
  totalCount: number;
  items: ReportHistoryItemSearchResponse[] | null;
};

export type EmployerEntityResponse = {
  id: string;
  employerId: string;
  employerName: string;
  name: string;
  code: string;
  poNumber: string | null;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  address4: string | null;
  address5: string | null;
  postcode: string | null;
};

export type SearchEmployerEntityResponse = {
  id: string;
  employerId: string;
  employerName: string;
  entityName: string;
  code: string;
  poNumber: string | null;
  address1: string | null;
  address2: string | null;
  address3: string | null;
  address4: string | null;
  address5: string | null;
  postcode: string | null;
  createdDate: string;
  updatedDate: string | null;
};

export type CreateEmployerEntityRequest = {
  employerId: string;
  entityName: string;
  code?: string;
  poNumber?: string;
  postcode: string;
  address1: string;
  address2?: string;
  address3?: string;
  address4?: string;
  address5?: string;
};

export type UpdateEmployerEntityRequest = {
  entityName: string;
  code?: string;
  poNumber?: string;
  postcode: string;
  address1: string;
  address2?: string;
  address3?: string;
  address4?: string;
  address5?: string;
};

export type EmployerEntityPagedResponse = {
  totalCount: number;
  items: SearchEmployerEntityResponse[];
};

export enum PartnerReportStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
}

export type PartnerReportDto = {
  status: PartnerReportStatus;
  errorMessage: string | null;
};
